import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import { service } from '@ember/service'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type SessionService from 'teacher-ui-client/services/session'
import { getSortViewModel } from 'teacher-ui-client/utils/sorting'
import { hash } from '@ember/helper'
import { t } from 'ember-intl'
import { sticky } from 'teacher-ui-client/modifiers/sticky'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { formatDuration } from 'teacher-ui-client/utils/format-duration'
import { trackedFunction } from 'reactiveweb/function'
import { keepLatest } from 'reactiveweb/keep-latest'
import { use } from 'ember-resources'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { Product } from 'teacher-ui-client/utils/products'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import type { BaseReport, BaseReportMetaPaging } from 'teacher-ui-client/services/reporting'
import { toRequestScope } from 'teacher-ui-client/services/reporting'
import gradeName from 'teacher-ui-client/helpers/grade-name'
import { BasePageSelector as PageSelector } from 'teacher-ui-client/components/base/page-selector'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import {
  serialize as serializeSort,
  deserialize as deserializeSort,
  updateSortColumns,
} from '@blakeelearning/data-tables/utils/sorting'
import { action } from '@ember/object'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { on } from '@ember/modifier'
import round from 'teacher-ui-client/helpers/round'
import joinList from 'teacher-ui-client/helpers/join-list'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { NoDataMessage } from 'teacher-ui-client/components/no-data-message'
import { ErrorsUnexpected } from 'teacher-ui-client/components/errors/unexpected'
import FidgetSpinnerWaveComponent from '@blakeelearning/fidget/components/fidget/spinner/wave'

interface Signature {
  Args: {
    uiScope: ModelFor<ReportingUiScopeRoute>['uiScope']
    period: ModelFor<ReportingUiScopeRoute>['period']
  }
}

interface StudentStats {
  id: string
  first_name: string
  last_name: string
  external_id: Nullable<string>
  school_class_names: string[]
  teacher_names: string[]
  grade_position: number
  writing_pieces_completed?: number
  long_write_activities_completed?: number
  min_lesson_mastered?: number
  max_lesson_mastered?: number
  lessons_mastered?: number
  map_quiz_attempts_count?: number
  map_quiz_average_score?: number
  book_activities_completed?: number
  total_usage?: number
  total_time?: number
}

export interface ReportData extends BaseReport {
  aggregates: {
    writing_pieces_completed: number
    long_write_activities_completed: number
    lessons_mastered: number
    map_quiz_attempts_count: number
    map_quiz_average_score: number
    book_activities_completed: number
    total_usage: number
    total_time: number
  }
  students: StudentStats[]
  meta: BaseReportMetaPaging
}

// box shadow provides a way to get a border between the sticky header cells
const stickyShadowRight = 'shadow-[inset_-1px_0_0] shadow-dusty-black-100'

export default class WritingLegendsDasboardTable extends Component<Signature> {
  @service session!: SessionService

  @service reporting!: ReportingService

  @queryParam('reporting/ui-scope/writing-legends/dashboard-table') pageNumber!: number

  @queryParam('reporting/ui-scope/writing-legends/dashboard-table') perPage!: number

  @queryParam('reporting/ui-scope/writing-legends/dashboard-table') sort!: string

  fetchReport = trackedFunction(this, () => {
    const requestScope = toRequestScope(this.args.uiScope)

    const url = this.reporting.buildGravityUrl({
      product: Product.WL,
      requestScope,
      reportName: 'dashboard/statistics/students-table',
    })

    const query = this.reporting.buildQuery({
      sort: this.sort,
      page: {
        number: this.pageNumber,
        size: this.perPage,
      },
      period: this.args.period,
    })

    return this.reporting.fetchReport<ReportData>(url, query)
  })

  @use
  latestReport = keepLatest({
    value: () => this.fetchReport.value,
    when: () => this.fetchReport.isPending,
  })

  get pageCount() {
    return this.latestReport?.meta?.total_pages
  }

  get itemCount() {
    return this.latestReport?.meta?.total_entries
  }

  get aggregates() {
    return this.latestReport?.aggregates
  }

  get studentRows() {
    // show empty message if no aggregates
    if (Object.values(this.latestReport?.aggregates ?? {}).every((aggregate) => !aggregate)) {
      return []
    }
    return this.latestReport?.students ?? []
  }

  get isSubco() {
    return this.session.currentTeacher.subcoFor(SubscriptionType.Writing)
  }

  get detailsColspan() {
    return this.isSubco ? 3 : 1
  }

  <template>
    {{#if this.studentRows}}
      <div class="overflow-x-auto print:max-h-[unset] print:overflow-visible">
        <table
          class="table-print-zoom-1/2 divide-dusty-black-100 border-dusty-black-100 relative h-fit w-full divide-y border-b print:divide-black"
          data-test-dashboard-table
          exportable
        >
          <thead class="divide-dusty-black-100 divide-y print:divide-black">
            <tr class="text-dusty-black-250 bg-white text-center font-semibold">
              <th
                class="bg-inherit p-3 font-semibold uppercase {{stickyShadowRight}}"
                colspan="2"
                {{sticky column=true z=1}}
              >
                {{t "student"}}
              </th>
              <div class="divide-dusty-black-100 contents divide-x align-[inherit]">
                <th colspan={{this.detailsColspan}}></th>
                <th class="p-3 font-semibold">
                  {{t "components.reporting.tables.writingLegends.dashboard.headers.excitingWriting" htmlSafe=true}}
                </th>
                <th class="p-3 font-semibold">
                  {{t "components.reporting.tables.writingLegends.dashboard.headers.longWrites" htmlSafe=true}}
                </th>
                <th class="p-3 font-semibold" colspan="3">
                  {{t "components.reporting.tables.writingLegends.dashboard.headers.lessons"}}
                </th>
                <th class="p-3 font-semibold" colspan="2">
                  {{t "components.reporting.tables.writingLegends.dashboard.headers.mapQuizzes"}}
                </th>
                <th class="p-3 font-semibold">
                  {{t "components.reporting.tables.writingLegends.dashboard.headers.books"}}
                </th>
                <th class="p-3 font-semibold whitespace-nowrap">
                  {{t "components.reporting.tables.writingLegends.dashboard.headers.totalUsage"}}
                  <UsageTooltip class="self-start" />
                </th>
                <th class="p-3 font-semibold">
                  {{t "components.reporting.tables.writingLegends.dashboard.headers.timeInProgram"}}
                </th>
              </div>
            </tr>
            <tr class="text-dusty-black-250 bg-white font-normal">
              {{! width of sticky columns MUST be locked - sticky modifier does not currently observe width changes of other cells, so does not update the offset }}
              <ThSort
                class="w-40 min-w-40 bg-inherit"
                {{sticky column=true z=1}}
                @sortKey="first_name"
                @text={{t "reporting.dashboardTables.firstName" htmlSafe=true}}
              />
              <ThSort
                class="w-40 min-w-40 bg-inherit {{stickyShadowRight}}"
                {{sticky column=true z=1}}
                @sortKey="last_name"
                @text={{t "reporting.dashboardTables.lastName" htmlSafe=true}}
              />
              <ThSort @sortKey="school_class_names" @text={{t "reporting.dashboardTables.classes"}} />
              {{#if this.isSubco}}
                <ThSort @sortKey="teacher_names" @text={{t "reporting.dashboardTables.teacher"}} />
                <ThSort @sortKey="grade_position" @text={{t "grade.label"}} />
              {{/if}}
              <ThSort
                class="border-dusty-black-100 border-l"
                @sortKey="writing_pieces_completed"
                @text={{t
                  "components.reporting.tables.writingLegends.dashboard.headers.writingPiecesCompleted"
                  htmlSafe=true
                }}
              />
              <ThSort
                class="border-dusty-black-100 border-l"
                @sortKey="long_write_activities_completed"
                @text={{t
                  "components.reporting.tables.writingLegends.dashboard.headers.writingPiecesCompleted"
                  htmlSafe=true
                }}
              />
              <ThSort
                class="border-dusty-black-100 border-l"
                @sortKey="min_lesson_mastered"
                @text={{t "components.reporting.tables.writingLegends.dashboard.headers.initialLesson"}}
              />
              <ThSort
                @sortKey="max_lesson_mastered"
                @text={{t
                  "components.reporting.tables.writingLegends.dashboard.headers.highestLessonCompleted"
                  htmlSafe=true
                }}
              />
              <ThSort
                @sortKey="lessons_mastered"
                @text={{t "components.reporting.tables.writingLegends.dashboard.headers.totalLessonsCompleted"}}
              />
              <ThSort
                class="border-dusty-black-100 border-l"
                @sortKey="map_quiz_attempts_count"
                @text={{t
                  "components.reporting.tables.writingLegends.dashboard.headers.numberOfAttempts"
                  htmlSafe=true
                }}
              />
              <ThSort
                @sortKey="map_quiz_average_score"
                @text={{t "components.reporting.tables.writingLegends.dashboard.headers.avgMapQuizScore" htmlSafe=true}}
              />
              <ThSort
                class="border-dusty-black-100 border-l"
                @sortKey="book_activities_completed"
                @text={{t "components.reporting.tables.writingLegends.dashboard.headers.activitiesCompleted"}}
              />
              <ThSort class="border-dusty-black-100 border-l" @sortKey="total_usage" />
              <ThSort
                class="border-dusty-black-100 border-l"
                @sortKey="total_time"
                @text={{t "components.reporting.tables.writingLegends.dashboard.headers.totalHours"}}
              />
            </tr>
            <tr class="bg-white text-black {{if this.fetchReport.isPending 'opacity-50'}}" data-test-summary-row>
              <th class="bg-inherit p-3" colspan="2" {{sticky column=true z=1}}>
                {{t "reporting.dashboardTables.totals"}}
              </th>
              <th class="p-3" colspan={{this.detailsColspan}}></th>
              <th class="p-3" data-test-summary-cell="writing_pieces_completed">
                {{formatValue this.aggregates.writing_pieces_completed}}
              </th>
              <th class="p-3" data-test-summary-cell="long_write_activities_completed">
                {{formatValue this.aggregates.long_write_activities_completed}}
              </th>
              <th />
              <th />
              <th class="p-3" data-test-summary-cell="lessons_mastered">
                {{formatValue this.aggregates.lessons_mastered}}
              </th>
              <th class="p-3" data-test-summary-cell="map_quiz_attempts_count">
                {{formatValue this.aggregates.map_quiz_attempts_count}}
              </th>
              <th class="p-3" data-test-summary-cell="map_quiz_average_score">
                {{#if this.aggregates.map_quiz_average_score}}
                  {{formatValue (round this.aggregates.map_quiz_average_score) (hash append="%")}}
                {{else}}
                  {{formatValue this.aggregates.map_quiz_average_score (hash append="%")}}
                {{/if}}
              </th>
              <th class="p-3" data-test-summary-cell="book_activities_completed">
                {{formatValue this.aggregates.book_activities_completed}}
              </th>
              <th class="p-3" data-test-summary-cell="total_usage">
                {{formatValue this.aggregates.total_usage}}
              </th>
              <th class="p-3" data-test-summary-cell="total_time">
                {{formatDuration this.aggregates.total_time}}
              </th>
            </tr>
          </thead>
          <tbody class="divide-dusty-black-100 relative divide-y {{if this.fetchReport.isPending 'opacity-50'}}">
            {{#each this.studentRows as |student index|}}
              <tr class="bg-white" data-test-table-row={{index}}>
                <td
                  class="text-wl-blue-300 min-w-0 bg-inherit p-3 font-bold wrap-anywhere"
                  data-test-cell="first_name"
                  {{sticky column=true z=1}}
                >
                  {{student.first_name}}
                </td>
                <td
                  class="text-wl-blue-300 min-w-0 bg-inherit p-3 font-bold wrap-anywhere"
                  data-test-cell="last_name"
                  {{sticky column=true z=1}}
                >
                  {{student.last_name}}
                </td>
                <td class="p-3" data-test-cell="school_class_names">
                  {{joinList student.school_class_names ", "}}
                </td>
                {{#if this.isSubco}}
                  <td class="p-3" data-test-cell="teacher_names">
                    {{joinList student.teacher_names ", "}}
                  </td>
                  <td class="p-3" data-test-cell="grade_position">
                    {{gradeName student.grade_position}}
                  </td>
                {{/if}}
                <td class="p-3" data-test-cell="writing_pieces_completed">
                  {{formatValue student.writing_pieces_completed}}
                </td>
                <td class="p-3" data-test-cell="long_write_activities_completed">
                  {{formatValue student.long_write_activities_completed}}
                </td>
                <td class="p-3" data-test-cell="min_lesson_mastered">
                  {{formatValue student.min_lesson_mastered}}
                </td>
                <td class="p-3" data-test-cell="max_lesson_mastered">
                  {{formatValue student.max_lesson_mastered}}
                </td>
                <td class="p-3" data-test-cell="lessons_mastered">
                  {{formatValue student.lessons_mastered}}
                </td>
                <td class="p-3" data-test-cell="map_quiz_attempts_count">
                  {{formatValue student.map_quiz_attempts_count}}
                </td>
                <td class="p-3" data-test-cell="map_quiz_average_score">
                  {{#if student.map_quiz_average_score}}
                    {{formatValue (round student.map_quiz_average_score) (hash append="%")}}
                  {{else}}
                    {{formatValue student.map_quiz_average_score (hash append="%")}}
                  {{/if}}
                </td>
                <td class="p-3" data-test-cell="book_activities_completed">
                  {{formatValue student.book_activities_completed}}
                </td>
                <td class="p-3" data-test-cell="total_usage">
                  {{formatValue student.total_usage}}
                </td>
                <td class="p-3" data-test-cell="total_time">
                  {{formatDuration student.total_time "-"}}
                </td>
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
      {{#if this.pageCount}}
        <PageSelector class="mx-2 my-3" @pageCount={{this.pageCount}} @itemCount={{this.itemCount}} />
      {{/if}}
    {{else if this.fetchReport.isPending}}
      <div class="m-4" data-test-dashboard-loading><FidgetSpinnerWaveComponent @centered={{true}} /></div>
    {{else if this.fetchReport.isError}}
      <ErrorsUnexpected class="m-4" data-test-dashboard-error />
    {{else}}
      <NoDataMessage class="m-4" data-test-dashboard-empty />
    {{/if}}
  </template>
}

interface ThSortSignature {
  Element: HTMLTableCellElement
  Args: {
    text?: string
    sortKey: string
  }
}

class ThSort extends Component<ThSortSignature> {
  @queryParam('reporting/ui-scope/writing-legends/dashboard-table') sort!: string

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  set sortingConfig(newSortConfig) {
    this.sort = serializeSort(newSortConfig)
  }

  @action
  updateSort() {
    this.sortingConfig = updateSortColumns(this.sortingConfig, this.args.sortKey)
  }

  get sortViewModel() {
    return getSortViewModel(this.sortingConfig, this.args.sortKey)
  }

  <template>
    <th
      class="select-none"
      data-test-sort-key={{@sortKey}}
      aria-sort={{this.sortViewModel.ariaDirection}}
      ...attributes
    >
      <button
        type="button"
        class="flex h-full w-full items-center gap-x-3 p-3 text-left {{if @text 'justify-between' 'justify-end'}}"
        {{on "click" this.updateSort}}
      >
        {{#if @text}}<span class="text-dusty-black-250 font-normal uppercase">{{@text}}</span>{{/if}}
        <PhosphorIcon @icon={{this.sortViewModel.icon}} class="text-dusty-black-400 print:hidden" />
      </button>
    </th>
  </template>
}

interface UsageTooltipSignature {
  Element: HTMLSpanElement
}

const UsageTooltip: TOC<UsageTooltipSignature> = <template>
  <span data-test-usage-tooltip class="print:hidden" ...attributes>
    <PhosphorIcon @icon="phosphor-info-fill" class="text-dusty-black-300 cursor-pointer" />
    <Tooltip @text={{t "components.reporting.tables.writingLegends.dashboard.tooltips.usage"}} />
  </span>
</template>
